/**
 * Fetches the backend's canonical search-field catalog and hydrates the
 * frontend's FIELD_CATALOG with it. Called once after authentication so
 * the dashboard's autocomplete + chip editor + raw_query parser stay in
 * lockstep with the backend's routing rules.
 *
 * Backend source of truth:
 *   doc2-api/routers/document_process/search_fields_catalog.py
 *   GET /document/search/fields → { version, fields: [...], capabilities: {...} }
 *
 * Capabilities tell the frontend whether the org has fulltext / vector
 * search enabled. The user-stated rule (Bug-2026-04-29): when the org
 * doesn't have fulltext enabled, the frontend MUST NOT send
 * is_fulltext / is_vector flags — the customer doesn't have that
 * option and the backend would otherwise route into a fulltextsearch
 * that has no index for them and the search hangs.
 *
 * Failure mode is silent: on network error / 5xx the frontend keeps the
 * shipped hardcoded FIELD_CATALOG (the fallback) and assumes
 * `fulltext_enabled = true` (legacy behavior, the historical default).
 * Never blocks the dashboard from rendering.
 */

import { ref, type Ref } from 'vue';

import { api } from 'src/pages/APIs';
import { whenRegionReady } from 'src/pages/api-base-url';
import {
  hydrateFieldCatalog,
  setExtendedFieldNames,
  type BackendFieldEntry,
} from 'src/pages/Dashboard/Filters/searchQueryParser';
import { useAuthTokenStore } from 'src/store/main';

export interface OrgSearchCapabilities {
  fulltext_enabled: boolean;
  vector_enabled: boolean;
}

interface CatalogResponse {
  version: number;
  fields: BackendFieldEntry[];
  capabilities?: OrgSearchCapabilities;
  // List of field names that the backend has projected from
  // document_extracted_data into typed ClickHouse columns
  // (SYNC_COLUMNS source of truth). Frontend uses this to tag dynamic
  // CUSTOM doc-type fields (which aren't in the static FIELD_CATALOG)
  // as 'extended' instead of 'fulltext' when they happen to match.
  // Optional for back-compat — older backends omit it; frontend then
  // treats every custom field as 'fulltext' (= the previous default).
  extended_field_names?: string[];
}

// Module-level reactive ref — every consumer that imports
// `searchCapabilities` sees the same singleton. Default: assume fulltext
// is on (legacy behavior). Real value is set after the first successful
// catalog fetch.
const _capabilities: Ref<OrgSearchCapabilities> = ref({
  fulltext_enabled: true,
  vector_enabled: true,
});

export const searchCapabilities: Readonly<Ref<OrgSearchCapabilities>> =
  _capabilities;

/**
 * Plain accessor used by code that runs outside Vue's reactive scope —
 * notably `computeQuickSearchSlots`, which is called from the chip
 * editor's onParsed and from filter-store builders. Returning the raw
 * object (not a ref) keeps that call site dependency-free.
 */
export function getSearchCapabilities(): OrgSearchCapabilities {
  return _capabilities.value;
}

let _loadedOnce = false;
let _inFlight: Promise<boolean> | null = null;

/**
 * Fetch the catalog once per app session. Subsequent calls return the
 * same promise (de-duped) so multiple concurrent callers cause only one
 * HTTP request. Resolves true when the catalog was hydrated, false on
 * any failure or empty response.
 */
export async function loadCatalogFromBackend(): Promise<boolean> {
  if (_loadedOnce) return true;
  if (_inFlight) return _inFlight;

  _inFlight = (async () => {
    try {
      const auth = useAuthTokenStore();
      const token = auth?.token;
      if (!token) return false;

      await whenRegionReady();
      const url = api.search_fields_catalog;
      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!resp.ok) {
        if (typeof console !== 'undefined') {
          console.warn(
            `[searchFieldsCatalog] Backend returned ${resp.status}; ` +
              `keeping shipped fallback FIELD_CATALOG.`,
          );
        }
        return false;
      }
      const body = (await resp.json()) as CatalogResponse;
      const ok = hydrateFieldCatalog(body?.fields ?? []);
      // Push the SYNC_COLUMN-derived extended-field set into the
      // parser module so customFieldsToFieldMetas can tag dynamic
      // doc-type fields with the correct category. Done BEFORE the
      // capability assignment so the very first onParsed tick sees
      // the right routing class for any custom field already in the
      // editor (saved-filter rehydration, URL-restored chips, …).
      if (Array.isArray(body?.extended_field_names)) {
        setExtendedFieldNames(body.extended_field_names);
      }
      if (body?.capabilities) {
        _capabilities.value = {
          fulltext_enabled: !!body.capabilities.fulltext_enabled,
          vector_enabled: !!body.capabilities.vector_enabled,
        };
      }
      if (ok) {
        _loadedOnce = true;
        if (typeof console !== 'undefined') {
          console.info(
            `[searchFieldsCatalog] Hydrated FIELD_CATALOG with ` +
              `${body.fields.length} fields (version ${body.version}) from backend. ` +
              `capabilities=${JSON.stringify(_capabilities.value)}`,
          );
        }
      }
      return ok;
    } catch (err) {
      if (typeof console !== 'undefined') {
        console.warn(
          '[searchFieldsCatalog] Fetch failed; keeping shipped fallback:',
          err,
        );
      }
      return false;
    } finally {
      _inFlight = null;
    }
  })();

  return _inFlight;
}

/** Test helper — clears the dedup state so unit tests can re-trigger. */
export function _resetCatalogLoaderForTests(): void {
  _loadedOnce = false;
  _inFlight = null;
  _capabilities.value = { fulltext_enabled: true, vector_enabled: true };
}

/** Test helper — directly set caps without going through the API. */
export function _setCapabilitiesForTests(caps: OrgSearchCapabilities): void {
  _capabilities.value = { ...caps };
}
